import { ChangeEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { login, selectUser, verify2FA, resend2FA } from './features/user/userSlice';
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent, Divider,
    Grid,
    TextField,
    Typography
} from "@mui/material";
import { Localized, useLocalization } from "@fluent/react";
import { showError, showSuccess } from "./features/notifications/notificationsSlice";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useCallback } from 'react';
import { selectSettings, setLocalePersistent, Locale } from './features/settings/settingsSlice';
import { If } from "./app/If";
import { Refresh } from "@mui/icons-material";
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { APIError } from "./app/appTypes";
import { AuthPageLayout } from "./AuthPageLayout";
import { LanguageSelector } from "./LanguageSelector";
import { InternalMicrosoftIcon } from "./app/customIcons/InternalMicrosoftIcon";


const PREFIX = 'Login';

const classes = {
    root: `${PREFIX}-root`,
    paper: `${PREFIX}-paper`,
    logo: `${PREFIX}-logo`,
    text: `${PREFIX}-text`,
    backdrop: `${PREFIX}-backdrop`
};

export interface LoginAPIError {
    success: boolean
    token: null | string
    errors: string[]
}

export const LanguageDropdown = () => {
    const dispatch = useAppDispatch();
    const { locale } = useAppSelector(selectSettings);

    const handleLangChange = useCallback((e: SelectChangeEvent) => {
        dispatch(setLocalePersistent(e.target.value as Locale));
    }, [dispatch])

    return <FormControl sx={{ width: '80%' }} >
        <InputLabel id="app-language-label">Language/Język/Sprache</InputLabel>
        <Select
            labelId="app-language-label"
            id="app-language"
            value={locale}
            onChange={handleLangChange}
            label="Language/Język/Sprache"
            fullWidth
        >
            <MenuItem value={'pl'}>polski</MenuItem>
            <MenuItem value={'en-US'}>english</MenuItem>
            <MenuItem value={'de'}>deutsch</MenuItem>
        </Select>
    </FormControl>
}

// export const AppUserLoggedIn = 'app:login'
//
// export const dispatchUserLoggedIn = () => {
//     window.dispatchEvent(new CustomEvent<{}>(AppUserLoggedIn, {}))
// }

export const Login = () => {
    const dispatch = useAppDispatch()
    const { tokenState } = useAppSelector(selectUser)
    const { l10n } = useLocalization()
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    const [step, setStep] = useState<1 | 2>(1);

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [code2FA, setCode2FA] = useState("");
    const [lockResendButton, setLockResendButton] = useState<boolean>(false);

    const [mask, setMask] = useState<boolean>(false);
    const [timerSeconds, setTimerSeconds] = useState<number>(0);

    const onUsernameChange = (e: ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)
    const onPasswordChange = (e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)
    const onCodeChange = (e: ChangeEvent<HTMLInputElement>) => setCode2FA(e.target.value)

    const handleEnter = (event) => {
        if (event.key === 'Enter') {
            handleLoginClick()
        }
    };

    const handleBackToLogin = () => {
        setStep(1);
        setTimerSeconds(0);
    };

    const resetTimer = () => {
        setTimerSeconds(180);
    };

    useEffect(() => {
        if (timerSeconds <= 0) return;

        const interval = setInterval(() => {
            setTimerSeconds((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => clearInterval(interval);
    }, [timerSeconds]);


    const [requires2FA, setRequires2FA] = useState(false);
    const [emailFor2FA, setEmailFor2FA] = useState<string | null>(null);

    const getFallbackReturnUrl = () => {
        const previousLocation = location.state?.prevLocation;

        if (!previousLocation) {
            return "/";
        }

        return `${previousLocation.pathname ?? ""}${previousLocation.search ?? ""}${previousLocation.hash ?? ""}` || "/";
    };

    const handleMicrosoftLogin = () => {
        const returnUrl = searchParams.get("returnUrl") ?? getFallbackReturnUrl();

        window.location.href =
            `${process.env.REACT_APP_REST_API}api/AuthManagement/sso-login?returnUrl=${encodeURIComponent(returnUrl)}`;
    };

    const handleLoginClick = async () => {
        if (username && password) {

            setMask(true)


            try {
                const result = await dispatch(login({ username, password })).unwrap()

                if (result.requires2FA) {
                    setRequires2FA(true)
                    setEmailFor2FA(result.emailFor2FA)
                    setStep(2)
                    resetTimer()
                    setMask(false)
                    return
                }
                if (result.passwordExpired) {
                    navigate("/changepsw")
                    return
                }
            }
            catch (error) {
                const payload = error as APIError
                if (payload) {
                    switch (payload.kind) {
                        case "http":
                            if (payload.body) {
                                const apiResponse = JSON.parse(payload.body) as LoginAPIError
                                const [error] = apiResponse.errors
                                dispatch(showError(error ?? "error-login-failed"))
                            } else {
                                dispatch(showError("error-login-failed"))
                            }
                            break
                        case "connection":
                            dispatch(showError("connection-issue"))
                            break
                    }
                } else {
                    dispatch(showError("error"))
                }
                setMask(false)
            }
            finally {
                setMask(false)
            }
        }
    }

    const handleResend2FA = async () => {
        if (!emailFor2FA) {
            dispatch(showError("two-factor-email-missing"));
            return;
        }

        setLockResendButton(true);

        try {
            const resultAction = await dispatch(resend2FA({ email: emailFor2FA }));

            if (resend2FA.fulfilled.match(resultAction)) {
                dispatch(showSuccess("two-factor-code-sent"));
                resetTimer();
            }

            if (resend2FA.rejected.match(resultAction)) {
                const { payload } = resultAction;
                if (payload) {
                    switch (payload.kind) {
                        case "http":
                            if (payload.body) {
                                dispatch(showError(payload.body));
                            } else {
                                dispatch(showError("two-factor-code-send-failed"));
                            }
                            break;
                        case "connection":
                            dispatch(showError("connection-issue"));
                            break;
                    }
                } else {
                    dispatch(showError("error"));
                }
            }
        } catch (error) {
            dispatch(showError("error"));
        }

        setLockResendButton(false);
    };

    const handleVerify2FA = async () => {
        if (code2FA && username) {

            setMask(true)

            const resultAction = await dispatch(verify2FA({ email: username, code: code2FA }))

            if (verify2FA.rejected.match(resultAction)) {
                const { payload } = resultAction
                if (payload) {
                    switch (payload.kind) {
                        case "http":
                            if (payload.body) {
                                const apiResponse = JSON.parse(payload.body) as LoginAPIError
                                const [error] = apiResponse.errors
                                dispatch(showError(error ?? "error-login-failed"))
                            } else {
                                dispatch(showError("error-login-failed"))
                            }
                            break
                        case "connection":
                            dispatch(showError("connection-issue"))
                            break
                    }
                } else {
                    dispatch(showError("error"))
                }
                setMask(false)
            }
        }
    };



    useEffect(() => {
        if (tokenState?.tag === 'valid') {
            const returnUrl = searchParams.get("returnUrl")

            if (returnUrl) {
                navigate(returnUrl)
                return
            }

            const redirectDestination = location.state?.prevLocation ?? { pathname: "/" }
            navigate(redirectDestination)
        }
    }, [tokenState, location, navigate, searchParams])

    if (tokenState && tokenState.tag === 'valid') {
        return null
    } else {
        return (
            <AuthPageLayout>
                <If condition={step === 1}>
                    <Grid container direction="column" spacing={2}>
                        <Grid item>
                            <Typography variant="h6" fontWeight="bold" textAlign="center">
                                <Localized id="logging">
                                    <span>Logowanie</span>
                                </Localized>
                            </Typography>
                        </Grid>

                        <Grid item>
                            <TextField
                                fullWidth
                                onChange={onUsernameChange}
                                label={l10n.getString("user", null, "Użytkownik")}
                                type="email"
                                autoComplete="email"
                                aria-required="true"
                            />
                        </Grid>

                        <Grid item>
                            <TextField
                                fullWidth
                                type="password"
                                autoComplete="current-password"
                                aria-required="true"
                                onChange={onPasswordChange}
                                onKeyDown={handleEnter}
                                label={l10n.getString("password", null, "Hasło")}
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={handleLoginClick}
                                color="primary"
                                size="large"
                                fullWidth
                                variant="contained"
                            >
                                <Localized id="login">
                                    <span>Login</span>
                                </Localized>
                            </Button>
                        </Grid>
                        <Grid item textAlign="center">
                            <Link to="/forgotpsw" style={{ fontSize: '0.9rem', color: '#1976d2' }}>
                                <Localized id="forgot-password-question">
                                    <span>Forgot password?</span>
                                </Localized>
                            </Link>
                        </Grid>

                        <Divider style={{ marginLeft: 30, marginRight: 20, marginTop: 20, marginBottom: 20 }} >
                            <Typography variant="subtitle1" style={{ padding: '0 10px', color: '#999' }}>
                                <Localized id="or">
                                    <span>_OR</span>
                                </Localized>
                            </Typography>
                        </Divider>
                        <Grid item>
                            <Button
                                onClick={handleMicrosoftLogin}
                                size="large"
                                fullWidth
                                variant="outlined"
                                startIcon={<InternalMicrosoftIcon />}
                                sx={{
                                    backgroundColor: '#FFFFFF',
                                    textTransform: 'none',
                                    padding: '10px 20px',
                                    '&:hover': {
                                        borderColor: '#000000',
                                    }
                                }}
                            >
                                <Localized id="login-sso-microsoft">
                                    <span>_Microsoft login</span>
                                </Localized>
                            </Button>

                        </Grid>
                        <Divider style={{ marginLeft: 30, marginRight: 20, marginTop: 20, marginBottom: 10 }} />

                        <Grid
                            item
                            textAlign="center"
                            sx={{
                                bgcolor: 'grey.100',
                                borderRadius: 2,
                                py: 1.5,
                                marginLeft: 2

                            }}
                        >
                            <Typography
                                variant="body2"
                                component="div"
                                sx={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}
                            >
                                <Localized id="no-account">
                                    <Box component="span" sx={{ display: 'inline-block', fontWeight: '800', transform: 'translateY(2px)' }}>
                                        Nie masz konta?
                                    </Box>
                                </Localized>{" "}
                                <Button
                                    component={Link}
                                    to="/register"
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    sx={{
                                        ml: 2,
                                        px: 1.25,
                                        py: 0.25,
                                        minWidth: 'auto',
                                        textTransform: 'none',
                                        display: 'inline-flex',
                                    }}
                                >
                                    <Localized id="sign-up">
                                        <span>Zarejestruj się</span>
                                    </Localized>
                                </Button>
                            </Typography>
                        </Grid>



                        <Divider style={{ marginLeft: 30, marginRight: 20, marginTop: 10, marginBottom: 20 }} />
                        <Grid item textAlign="center">
                            <LanguageSelector />
                        </Grid>
                    </Grid>
                </If>
                <If condition={step === 2 && requires2FA}>
                    <Grid container direction="column" spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                <Localized id="two-factor-title">
                                    <span>_Weryfikacja dwuetapowa</span>
                                </Localized>
                            </Typography>
                            <Typography variant="body1" color="textSecondary">
                                <Localized id="two-factor-info">
                                    <span>_Kod send {emailFor2FA}</span>
                                </Localized>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                type="text"
                                aria-required="true"
                                autoComplete="one-time-code"
                                onChange={onCodeChange}
                                label={l10n.getString("code", null, "_Kod")}
                                inputProps={{ maxLength: 6 }}
                                sx={{ letterSpacing: "0.3em", textAlign: "center" }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button onClick={handleVerify2FA} color="primary" size="large" variant="contained">
                                <Localized id="confirm-2fa">
                                    <span>_Potwierdź</span>
                                </Localized>
                            </Button>
                        </Grid>
                        <Divider style={{ marginLeft: 30, marginRight: 20, marginTop: 20, marginBottom: 20 }} />
                        <Grid container justifyContent="space-between" alignItems="center" >
                            <Grid item>
                                <Button
                                    onClick={handleBackToLogin}
                                    color="primary"
                                    size="small"
                                    startIcon={<ArrowLeftIcon />}
                                    sx={{ textTransform: "none", marginLeft: 3 }}
                                >
                                    <Localized id="back-to-login">
                                        <span>_Wstecz</span>
                                    </Localized>
                                </Button>
                            </Grid>
                            <Grid item>
                                <Typography variant="body2" sx={{ mr: 2, fontWeight: 'bold', color: timerSeconds > 0 ? '#1976d2' : 'textSecondary' }}>
                                    {timerSeconds > 0 ? `${Math.floor(timerSeconds / 60)}:${String(timerSeconds % 60).padStart(2, '0')}` : ''}
                                </Typography>
                                <Button
                                    onClick={handleResend2FA}
                                    color="primary"
                                    size="small"
                                    endIcon={<Refresh />}
                                    disabled={lockResendButton || timerSeconds > 0}
                                    sx={{ textTransform: "none", marginRight: 1 }}
                                >
                                    <Localized id="resend-code">
                                        <span>_Wyślij nowy kod</span>
                                    </Localized>
                                </Button>
                            </Grid>
                        </Grid>

                        <Grid item textAlign="center">
                            <LanguageSelector />
                        </Grid>
                    </Grid>
                </If>

                <Backdrop className={classes.backdrop} open={mask}>
                    <CircularProgress color="inherit"></CircularProgress>
                </Backdrop>
            </AuthPageLayout>
        )
    }
}

export const LoginDialog = () => {
    return <Dialog open={true} fullScreen fullWidth>
        <DialogContent sx={{ p: 0 }}>
            <Login />
        </DialogContent>
    </Dialog>
}